import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import { fetchPortfolios, fetchHoldingsWithValues, fetchPortfolioValueHistory, fetchSectorAllocation, type HoldingWithValue, type Portfolio } from '../lib/data'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

type TimeRange = '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL'

const TIME_RANGES: TimeRange[] = ['1W', '1M', '3M', '6M', '1Y', 'ALL']

function getCutoffDate(range: TimeRange): Date {
  const now = new Date()
  const d = new Date(now)
  switch (range) {
    case '1W':
      d.setDate(now.getDate() - 7)
      break
    case '1M':
      d.setMonth(now.getMonth() - 1)
      break
    case '3M':
      d.setMonth(now.getMonth() - 3)
      break
    case '6M':
      d.setMonth(now.getMonth() - 6)
      break
    case '1Y':
      d.setFullYear(now.getFullYear() - 1)
      break
    case 'ALL':
    default:
      return new Date('2000-01-01')
  }
  return d
}

const SECTOR_COLORS = [
  '#4f46e5', '#06b6d4', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#6366f1', '#14b8a6', '#f97316',
  '#84cc16', '#d946ef', '#3b82f6', '#22c55e', '#eab308',
]

function Portfolio() {
  const { id } = useParams<{ id: string }>()
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null)
  const [holdings, setHoldings] = useState<HoldingWithValue[]>([])
  const [valueHistory, setValueHistory] = useState<{ date: string; value: number }[]>([])
  const [sectorAllocation, setSectorAllocation] = useState<{ sector: string; value: number; pct: number }[]>([])
  const [timeRange, setTimeRange] = useState<TimeRange>('1Y')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      loadPortfolioData()
    }
  }, [id])

  async function loadPortfolioData() {
    try {
      setLoading(true)

      // Load portfolio details
      const portfolios = await fetchPortfolios()
      const currentPortfolio = portfolios.find(p => p.id === id)
      if (!currentPortfolio) {
        throw new Error('Portfolio not found')
      }
      setPortfolio(currentPortfolio)

      // Load holdings with prices
      const holdingsData = await fetchHoldingsWithValues(id!)
      setHoldings(holdingsData)

      // Load portfolio value history
      const historyData = await fetchPortfolioValueHistory(id!)
      setValueHistory(historyData)

      // Load sector allocation
      const sectorData = await fetchSectorAllocation(id!)
      setSectorAllocation(sectorData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolio')
    } finally {
      setLoading(false)
    }
  }

  // Filter value history by time range
  const filteredValueHistory = (() => {
    if (timeRange === 'ALL') return valueHistory
    const cutoff = getCutoffDate(timeRange)
    return valueHistory.filter(d => new Date(d.date) >= cutoff)
  })()

  // Calculate totals
  const totalValue = holdings.reduce((sum, h) => sum + h.currentValue, 0) + (portfolio?.cash_balance || 0)
  const totalCost = holdings.reduce((sum, h) => sum + h.cost_basis, 0)
  const totalGain = totalValue - totalCost - (portfolio?.cash_balance || 0)
  const totalGainPct = totalCost > 0 ? (totalGain / totalCost) * 100 : 0
  const totalDayChange = holdings.reduce((sum, h) => sum + h.dayChange, 0)

  if (loading) {
    return (
      <div className="app-container">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  if (error || !portfolio) {
    return (
      <div className="app-container">
        <div className="flex items-center gap-2 mb-4">
          <Link to="/" className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-xl font-bold">Error</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error || 'Portfolio not found'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="app-container">
      {/* Back button and title */}
      <div className="flex items-center gap-2 mb-4">
        <Link to="/" className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-xl font-bold">{portfolio.name}</h1>
      </div>

      {/* Portfolio Summary */}
      <div className="mb-6">
        <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalValue)}</p>
        <div className="flex items-center gap-4 mt-2 text-sm">
          <span className="text-gray-500">Cost: {formatCurrency(totalCost)}</span>
          <span className={totalGain >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
            Gain: {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain)} ({totalGainPct >= 0 ? '+' : ''}{totalGainPct.toFixed(1)}%)
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">Cash: {formatCurrency(portfolio.cash_balance)}</p>
        {totalDayChange !== 0 && (
          <div className={`text-sm mt-1 ${totalDayChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {totalDayChange >= 0 ? '▲' : '▼'} {formatCurrency(Math.abs(totalDayChange))} today
          </div>
        )}
      </div>

      {/* Portfolio Value History Chart */}
      {valueHistory.length > 0 && (
        <div className="card mb-6">
          <h3 className="text-sm font-medium text-gray-600 mb-3">Portfolio Value Over Time</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={filteredValueHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-GB', { month: 'short' })}
                  tick={{ fontSize: 11 }}
                  stroke="#9ca3af"
                />
                <YAxis
                  tickFormatter={(value) => `£${(value / 1000).toFixed(1)}k`}
                  tick={{ fontSize: 11 }}
                  stroke="#9ca3af"
                  width={50}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(value as number), 'Value']}
                  labelFormatter={(label) => new Date(label as string).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: '2-digit',
                  })}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#4f46e5"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Time range selector */}
          <div className="flex justify-center gap-1 mt-3">
            {TIME_RANGES.map((period) => (
              <button
                key={period}
                onClick={() => setTimeRange(period)}
                className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                  timeRange === period
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Asset Allocation Pie Chart */}
      {sectorAllocation.length > 0 && (
        <div className="card mb-6">
          <h3 className="text-sm font-medium text-gray-600 mb-3">Asset Allocation</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sectorAllocation}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="sector"
                  stroke="none"
                >
                  {sectorAllocation.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={SECTOR_COLORS[index % SECTOR_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => {
                    const entry = sectorAllocation.find(s => s.value === (value as number))
                    const pct = entry?.pct ?? 0
                    return [formatCurrency(value as number), `${entry?.sector ?? ''} (${pct.toFixed(1)}%)`]
                  }}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                  formatter={(value: string, entry: any) => (
                    `${value} (${entry.payload.pct.toFixed(1)}%)`
                  )}
                  wrapperStyle={{ fontSize: '11px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Sort options */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-gray-500">Sort:</span>
        <button className="text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
          Value ▼
        </button>
        <button className="text-sm font-medium text-gray-600 px-3 py-1 rounded-full hover:bg-gray-100">
          Gain %
        </button>
        <button className="text-sm font-medium text-gray-600 px-3 py-1 rounded-full hover:bg-gray-100">
          Name
        </button>
      </div>

      {/* Holdings List */}
      <div className="space-y-3">
        {holdings.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No holdings yet.</p>
            <p className="text-sm mt-1">Import a CSV to add holdings.</p>
          </div>
        ) : (
          holdings.filter(h => h.quantity > 0).map((holding) => (
            <Link
              key={holding.ticker}
              to={`/holding/${id}/${holding.ticker}`}
              className="card block hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${holding.gain >= 0 ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="font-mono font-bold text-gray-700">{holding.ticker}</span>
                  <span className="text-gray-600 truncate max-w-[150px]">{holding.name}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold">{formatCurrency(holding.currentValue)}</span>
                <span className={`text-sm ${holding.gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {holding.gain >= 0 ? '+' : ''}{formatCurrency(holding.gain)} ({holding.gainPct >= 0 ? '+' : ''}{holding.gainPct.toFixed(1)}%)
                </span>
              </div>
              {holding.quantity > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  {holding.quantity.toLocaleString()} units @ {holding.priceInPence ? `£${(holding.priceInPence / 100).toFixed(2)}` : 'N/A'}
                </div>
              )}
            </Link>
          ))
        )}
      </div>

      {/* Empty state action */}
      {holdings.length === 0 && (
        <div className="mt-6">
          <Link to="/import" className="btn-primary w-full block text-center">
            📁 Import CSV
          </Link>
        </div>
      )}
    </div>
  )
}

export default Portfolio
